import { useCallback, useMemo, useState } from 'react';
import { useSearch } from '@contexts/SearchContext';
import { canSearch } from '@models/search/searchModel';

export function useGlobalSearchController(onSearchToggle) {
  const [query, setQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const { search, loading, error } = useSearch();

  const hasActiveSearch = canSearch(query);
  const results = useMemo(
    () => (hasActiveSearch ? search(query) : []),
    [hasActiveSearch, query, search]
  );

  const notifySearchToggle = useCallback((active) => {
    onSearchToggle?.(active);
  }, [onSearchToggle]);

  const handleToggle = useCallback(() => {
    const nextExpanded = !isExpanded;
    setIsExpanded(nextExpanded);

    if (!nextExpanded) {
      setQuery('');
    }

    notifySearchToggle(nextExpanded && canSearch(query));
  }, [isExpanded, notifySearchToggle, query]);

  const handleQueryChange = useCallback((nextQuery) => {
    setQuery(nextQuery);
    notifySearchToggle(canSearch(nextQuery));
  }, [notifySearchToggle]);

  const handleClear = useCallback(() => {
    setQuery('');
    notifySearchToggle(false);
  }, [notifySearchToggle]);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setQuery('');
    notifySearchToggle(false);
  }, [notifySearchToggle]);

  return {
    query,
    isExpanded,
    loading,
    error,
    results,
    hasActiveSearch,
    onToggle: handleToggle,
    onQueryChange: handleQueryChange,
    onClear: handleClear,
    onClose: handleClose
  };
}
